import React, { useEffect } from "react";
import { DateInput, TextFields } from "../../../utils/Custom/custominput";
import { useLocation, useParams } from "react-router-dom";
import { format } from "date-fns";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import moment from "moment";
import { AddButton, CancelButton, SaveAndUpdate, UploadButton } from "../../../utils/Custom/Button";
import { toast } from "react-toastify";
import { BASE_URL, reportLetter } from "../../../global";
import DownloadPdfLoaderModal from "../../../utils/commonComponent/downloadPdfLoader";
import { generalCorrespondentsPDf } from "./generalCorrespondentsPrint";
import { getPermissions } from "../rbac/hasAccess";

const FileUploadComponent = ({
  fileUploadResponse,
  setFileUploadResponse,
  label,
}) => {
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Allowed image types
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only image files (JPG, PNG, WEBP) are allowed!");
      return;
    }
    const data = new FormData();
    data.append("fileToUpload", file);

    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status === 200) {
          setFileUploadResponse(res.data);
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch((err) => {
        if (err.message !== "request_aborted")
          toast.error("Something Went Wrong!");
      });
  };

  return (
    <div className="w-full">
      <UploadButton
        label={label}
        onChange={handleFileUpload}
        fileUrl={fileUploadResponse}
      />
      <p style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>
        Only upload cropped image (Signature).<br /> Full page images are not allowed.
      </p>
    </div>
  );
};

const EditGeneralCorrespondents = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const [inputStates, setInputStates] = React.useState({});
  const [downloadPdfloading, setDownloadPdfloading] = React.useState(false);

  const location = useLocation();
  const isPrintMode = location.pathname.includes("/print/");

  const fieldCharLimits = {
    clientName: 315,           /*350*/
    subject: 230,              /*255*/
    content: 4900,              /*5000*/
  }

  const updateInputObject = (key, value) => {
    const Limit = fieldCharLimits[key];
    if (Limit && typeof value === "string" && value.length > Limit) {
      toast.warning(`You have reached the maximum ${Limit} allowed text limit`);
      return;
    }
    setInputStates((prevInputStates) => ({
      ...prevInputStates,
      [key]: value,
    }));
  };

  const fetchGeneralCorrespondentsDetails = async () => {
    try {
      const query = `select * from GeneralCorrespondents where id = ${params.id}`;
      const [priceList] = await fetchQuery(query);
      setInputStates(priceList);
    } catch (error) {
      console.error("Error fetching client:", error);
    }
  };

  const onSubmit = () => {
    // Check mandatory fields one by one
    if (!inputStates.letterNumber?.trim()) {
      toast.error("Please enter Letter Number!");
      return; // stop submission
    }

    if (!inputStates.clientName?.trim()) {
      toast.error("Please enter Client Name!");
      return; // stop submission
    }

    if (!inputStates.date) {
      toast.error("Please select Date!");
      return; // stop submission
    }

    const payload = {
      ...inputStates,
      date: inputStates?.date
        ? moment(inputStates?.date).format("YYYY-MM-DD")
        : null,
      createBy: localStorage.getItem("id"),
      status: 1,
    };

    const url =
      BASE_URL +
      `GeneralCorrespondents${params.id != 0 ? `/${params.id}` : ""}`;
    const request =
      params.id !== "0"
        ? axiosWithToken.patch(url, payload)
        : axiosWithToken.post(url, payload);

    request
      .then(() => {
        toast.success(
          params.id !== "0"
            ? "Report Updated Successfully!"
            : "Report Created Successfully!"
        );
        setTimeout(window.location.reload(), 500);
      })
      .catch((error) => console.error("Submit error:", error));
  };

  useEffect(() => {
    const setAutoLetterNumber = async () => {
      if (params.id == 0) {
        // const year = new Date().getFullYear(); // current year
        // only for new record
        try {
          const query = `SELECT letterNumber FROM GeneralCorrespondents WHERE status = 1 ORDER BY id DESC LIMIT 1`;
          const [lastRecord] = await fetchQuery(query);

          const lastNumber = lastRecord?.letterNumber
            ? parseInt(lastRecord.letterNumber.split("/")?.pop())
            : 0;

          const nextLetterNumber = `${reportLetter}/2026-27/${lastNumber + 1}`;
          updateInputObject("letterNumber", nextLetterNumber);
        } catch (error) {
          console.error("Error generating Letter Number:", error);
          updateInputObject("letterNumber", `${reportLetter}/2026-27/1`);
        }
      }
    };

    setAutoLetterNumber();
  }, [params.id]);

  useEffect(() => {
    if (params.id != 0) fetchGeneralCorrespondentsDetails();
  }, [params]);

  return (
    <div>
      <div className="flex justify-end">
        {isPrintMode && (permissions.pdfDownload || permissions.allAccess) && (
          <AddButton
            id="btn-print"
            label="Download PDF"
            onClick={async (e) => {
              await generalCorrespondentsPDf(
                inputStates,
                setDownloadPdfloading
              );
            }}
          />
        )}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-4">
        <TextFields
          label="Letter Number *"
          value={inputStates.letterNumber || ""}
          onChange={(e) => updateInputObject("letterNumber", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={true}
        />
        <DateInput
          id="date"
          label={"Date *"}
          value={inputStates.date ? new Date(inputStates.date) : ""}
          onChange={(date) => {
            const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
            updateInputObject("date", formattedDate);
          }}
          disabled={isPrintMode}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4">
        <TextFields
          label="Client Name *"
          value={inputStates.clientName || ""}
          onChange={(e) => updateInputObject("clientName", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={isPrintMode}
        />
        <TextFields
          label="Subject"
          value={inputStates.subject || ""}
          onChange={(e) => updateInputObject("subject", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={isPrintMode}
        />
        <TextFields
          label="Content"
          value={inputStates.content || ""}
          onChange={(e) => updateInputObject("content", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={isPrintMode}
        />
        <TextFields
          label="Name and Designation"
          value={inputStates.nameDesignation || ""}
          onChange={(e) => updateInputObject("nameDesignation", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={isPrintMode}
        />
        <TextFields
          label="Attachments"
          value={inputStates.attachments || ""}
          onChange={(e) => updateInputObject("attachments", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
          disabled={isPrintMode}
        />
        <FileUploadComponent
          fileUploadResponse={inputStates.signPath}
          setFileUploadResponse={(value) =>
            updateInputObject("signPath", value)
          }
          label="Upload Image"
        />
      </div>

      {!isPrintMode && (
        <div className="flex gap-10 mt-5 justify-end ">
          <CancelButton
            id="btn-cancel"
            label="Cancel"
            outline
            onClick={() => {
              if (window.confirm("Are you sure you want to cancel?")) {
                window.history.back();
              }
            }}
          />
          {(permissions.save || permissions.create || permissions.allAccess) && (
            <SaveAndUpdate
              id="btn-add-outline"
              label={params.id == 0 ? "Save" : "Update"}
              onClick={onSubmit}
            />
          )}
        </div>
      )}

      {downloadPdfloading && (
        <DownloadPdfLoaderModal loading={downloadPdfloading} name={"Report"} />
      )}
    </div>
  );
};

export default EditGeneralCorrespondents;
